import { useCallback, useEffect, useMemo, useState } from "react";
import { DailyExpenseDay } from "../../types/daily-expense.type";
import {
  CategoryLayout,
  DailyExpenseFormState,
  OtherRow,
  buildFormState,
  computeFormTotals,
  emptyOtherRow,
  nextOtherId,
  tidyAmountText,
  validateForm,
} from "./dailyExpenseForm";

type FormUpdater = (form: DailyExpenseFormState) => DailyExpenseFormState;

/**
 * The S2 form's working copy of the day. It is rebuilt whenever a newer day arrives (a load or
 * the answer of a write), which also clears the unsaved flag. Tidying an amount on blur only
 * re-formats it, so it does not count as an edit.
 */
export function useDailyExpenseForm(
  day: DailyExpenseDay | null,
  layout: CategoryLayout | null,
) {
  const [form, setForm] = useState<DailyExpenseFormState | null>(null);
  const [isDirty, setDirty] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!day || !layout) return;
    setForm(buildFormState(day, layout, day.actions.can_edit));
    setDirty(false);
    setShowErrors(false);
  }, [day, layout]);

  const edit = useCallback((updater: FormUpdater) => {
    setForm((previous) => previous && updater(previous));
    setDirty(true);
  }, []);
  const tidy = useCallback((updater: FormUpdater) => {
    setForm((previous) => previous && updater(previous));
  }, []);

  const changeCell = (routeUuid: string, code: string, text: string) =>
    edit((current) => ({
      ...current,
      sales: {
        ...current.sales,
        [routeUuid]: {
          ...current.sales[routeUuid],
          [code]: { ...current.sales[routeUuid][code], text },
        },
      },
    }));
  const tidyCell = (routeUuid: string, code: string) =>
    tidy((current) => {
      const cell = current.sales[routeUuid][code];
      return {
        ...current,
        sales: {
          ...current.sales,
          [routeUuid]: { ...current.sales[routeUuid], [code]: { ...cell, text: tidyAmountText(cell.text) } },
        },
      };
    });
  const changeAdmin = (code: string, text: string) =>
    edit((current) => ({ ...current, admin: { ...current.admin, [code]: text } }));
  const tidyAdmin = (code: string) =>
    tidy((current) => ({
      ...current,
      admin: { ...current.admin, [code]: tidyAmountText(current.admin[code] ?? "") },
    }));
  const addOther = () =>
    edit((current) => ({
      ...current,
      others: [...current.others, emptyOtherRow(nextOtherId(current.others))],
    }));
  const changeOther = (id: number, patch: Partial<OtherRow>) =>
    edit((current) => ({
      ...current,
      others: current.others.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  const tidyOtherAmount = (id: number) =>
    tidy((current) => ({
      ...current,
      others: current.others.map((row) =>
        row.id === id ? { ...row, amountText: tidyAmountText(row.amountText) } : row,
      ),
    }));
  const removeOther = (id: number) =>
    edit((current) => ({
      ...current,
      others: current.others.filter((row) => row.id !== id),
    }));

  const errors = useMemo(() => (form ? validateForm(form) : null), [form]);
  const totals = useMemo(
    () => (form && layout ? computeFormTotals(form, layout) : null),
    [form, layout],
  );

  return {
    form,
    errors,
    totals,
    isDirty,
    showErrors,
    setShowErrors,
    changeCell,
    tidyCell,
    changeAdmin,
    tidyAdmin,
    addOther,
    changeOther,
    tidyOtherAmount,
    removeOther,
  };
}
